/*
 * LeadToClientCalculator
 * Uses only the four numbers the reader enters, for the same time period.
 *
 * Rules:
 * - Inputs: organic sessions (S), calls + form submissions (L), consultations (C), signed clients (K).
 *   Each must be a whole number >= 0. Results appear once all four are filled in.
 * - Each stage must not exceed the stage above it (L <= S, C <= L, K <= C); otherwise an
 *   error explains which number to recheck and no rates are shown.
 * - Rates: L/S, C/L, K/C and overall K/S, shown as percentages to one decimal place.
 *   A rate whose denominator is 0 shows "n/a".
 * - "Where to look first" compares people who already contacted the firm:
 *     lostBeforeConsult = L - C, lostAfterConsult = C - K.
 *   The larger one is named. If equal (and > 0) both are named. If both are 0, no leak is named.
 *   If L is 0, the reader is told to confirm tracking first instead.
 *   Visit-to-inquiry is not compared with these, because how much of a site's traffic is
 *   informational changes that rate far more than intake does. No benchmarks are used.
 * No tracking, storage or network calls.
 */
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
 
type FieldKey = "sessions" | "leads" | "consults" | "clients";
 
const FIELDS: { key: FieldKey; label: string; help: string; placeholder: string }[] = [
  { key: "sessions", label: "Organic search sessions", help: "From GA4, organic search only", placeholder: "e.g. 2400" },
  { key: "leads", label: "Calls + form submissions from organic", help: "Tracked calls, forms and chat clicks", placeholder: "e.g. 60" },
  { key: "consults", label: "Consultations held", help: "From your intake records", placeholder: "e.g. 25" },
  { key: "clients", label: "Signed clients", help: "From your case management or CRM", placeholder: "e.g. 8" },
];
 
function parse(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
}
 
function pct(num: number, den: number): string {
  if (den === 0) return "n/a";
  return `${((num / den) * 100).toFixed(1)}%`;
}
 
export default function LeadToClientCalculator() {
  const baseId = useId();
  const [values, setValues] = useState<Record<FieldKey, string>>({
    sessions: "",
    leads: "",
    consults: "",
    clients: "",
  });
 
  const S = parse(values.sessions);
  const L = parse(values.leads);
  const C = parse(values.consults);
  const K = parse(values.clients);
  const nums = [S, L, C, K];
 
  const anyEmpty = nums.some((n) => n === null);
  const anyInvalid = nums.some((n) => n !== null && Number.isNaN(n));
 
  let error: string | null = null;
  if (!anyEmpty && !anyInvalid && S !== null && L !== null && C !== null && K !== null) {
    if (L > S) error = "Calls + forms can't be higher than organic sessions. Recheck both numbers are from the same period.";
    else if (C > L) error = "Consultations can't be higher than calls + forms. Recheck that both only count organic inquiries.";
    else if (K > C) error = "Signed clients can't be higher than consultations. Recheck the client count.";
  }
 
  const ready = !anyEmpty && !anyInvalid && !error && S !== null && L !== null && C !== null && K !== null;
 
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
 
  let leak: { title: string; body: string; href: string; linkText: string }[] = [];
  let lostBefore = 0;
  let lostAfter = 0;
  if (ready && L !== null && C !== null && K !== null) {
    lostBefore = L - C;
    lostAfter = C - K;
    const before = {
      title: `Between inquiry and consultation (${lostBefore} lost)`,
      body: "People reached out but never got to a consultation. Check how fast inquiries are answered, whether calls are missed after hours, and whether the next step was clear.",
      href: "/services/lead-generation-for-law-firms",
      linkText: "Lead generation for law firms",
    };
    const after = {
      title: `Between consultation and signed client (${lostAfter} lost)`,
      body: "Consultations aren't turning into clients. Check whether organic searches are bringing the cases your firm actually takes, by page and by practice area.",
      href: "/law-firm-seo-audit",
      linkText: "Get a free SEO audit",
    };
    if (lostBefore > lostAfter) leak = [before];
    else if (lostAfter > lostBefore) leak = [after];
    else if (lostBefore > 0) leak = [before, after];
  }
 
  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">From search to signed client: your numbers</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Use the same time period for all four (for example, last quarter). Nothing you enter is saved or sent.
      </p>
 
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => {
          const id = `${baseId}-${f.key}`;
          const helpId = `${id}-help`;
          return (
            <div key={f.key}>
              <label htmlFor={id} className="block text-sm font-medium">
                {f.label}
              </label>
              <input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={values[f.key]}
                onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                aria-describedby={helpId}
                className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
              />
              <p id={helpId} className="mt-1 text-xs text-muted-foreground">
                {f.help}
              </p>
            </div>
          );
        })}
      </div>
 
      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {anyInvalid && <p className="text-sm">Please enter whole numbers of zero or more.</p>}
        {!anyInvalid && anyEmpty && (
          <p className="text-sm text-muted-foreground">Fill in all four numbers to see your stage-by-stage rates.</p>
        )}
        {error && <p className="text-sm">{error}</p>}
        {ready && S !== null && L !== null && C !== null && K !== null && (
          <div>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Visit → inquiry</dt>
                <dd className="text-lg font-semibold">{pct(L, S)}</dd>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Inquiry → consultation</dt>
                <dd className="text-lg font-semibold">{pct(C, L)}</dd>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Consultation → client</dt>
                <dd className="text-lg font-semibold">{pct(K, C)}</dd>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Visit → client (overall)</dt>
                <dd className="text-lg font-semibold">{pct(K, S)}</dd>
              </div>
            </dl>
 
            <div className="mt-4">
              <p className="text-sm font-semibold">Where to look first</p>
              {L === 0 ? (
                <p className="mt-1 text-sm">
                  No organic inquiries were recorded in this period. First confirm calls and forms are being tracked,
                  then check that your pages give visitors a clear next step.{" "}
                  <Link to={"/services/law-firm-marketing-analytics" as string} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                    Analytics &amp; reporting →
                  </Link>
                </p>
              ) : leak.length === 0 ? (
                <p className="mt-1 text-sm">
                  Everyone who reached out became a client in this period, so the gains are at the top: more of the
                  right visitors.{" "}
                  <Link to={"/services/law-firm-seo" as string} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                    Law firm SEO →
                  </Link>
                </p>
              ) : (
                <ul className="mt-2 space-y-3">
                  {leak.map((l) => (
                    <li key={l.title} className="rounded-md border border-border bg-card p-3 text-sm">
                      <p className="font-medium">{l.title}</p>
                      <p className="mt-1 text-muted-foreground">{l.body}</p>
                      <Link to={l.href} className={`mt-1 inline-block font-medium underline underline-offset-4 ${focusRing}`}>
                        {l.linkText} →
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                Visit → inquiry isn’t compared here: it depends mostly on how much of your traffic is informational.
                Check it page by page in{" "}
                <Link to={"/services/law-firm-marketing-analytics" as string} className={`underline underline-offset-4 ${focusRing}`}>
                  your reporting
                </Link>
                .
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
